import { Check, Scissors, Palette } from "lucide-react";
import { Window, Tag, seg } from "./primitives";

const CLIPS = [
  { img: "/images/re-agent-sign.jpg", w: 26, from: -60 },
  { img: "/images/re-house.jpg", w: 24, from: 80 },
  { img: "/images/re-interior.jpg", w: 22, from: -40 },
  { img: "/images/re-tour.jpg", w: 22, from: 70 },
];
const CAPTIONS = ["Selling your home?", "Price it right.", "Sell with confidence.", "Free valuation →"];

function Track({ label, children }) {
  return (
    <div className="grid grid-cols-[34px_1fr] items-center gap-2">
      <span className="font-mono text-[10px] text-slate-400">{label}</span>
      <div className="relative flex h-full gap-[2px] rounded-md bg-white/[0.03] p-[2px]">{children}</div>
    </div>
  );
}

export function StageEdit({ p }) {
  const done = p > 0.8;
  const grade = seg(p, 0.45, 0.75);
  return (
    <Window title="Rivanta Edit — Example_Realtor_Ad_v3" right={<Tag className={done ? "!text-emerald-300" : ""}>{done ? <><Check className="h-3 w-3" /> Exported</> : "Editing"}</Tag>}>
      <div className="grid h-full grid-rows-[1fr_auto] gap-3 p-3 sm:p-4">
        <div className="grid min-h-0 grid-cols-[auto_1fr] gap-4">
          <div className="relative aspect-[9/16] h-full overflow-hidden rounded-xl border border-white/10 bg-black">
            <img src="/images/re-agent-sign.jpg" alt="Example realtor ad preview" loading="lazy" className="h-full w-full object-cover"
              style={{ filter: `saturate(${0.5 + grade * 0.8}) contrast(${0.9 + grade * 0.2}) brightness(${0.85 + grade * 0.15})` }} />
            <p className="absolute inset-x-2 bottom-[18%] rounded bg-black/70 px-1.5 py-1 text-center text-[10px] sm:text-xs font-bold text-white"
              style={{ opacity: seg(p, 0.3, 0.4) }}>
              {CAPTIONS[Math.min(3, Math.floor(seg(p, 0.3, 0.95) * 4))]}
            </p>
            {done && <span className="absolute left-2 top-2 rounded-full bg-electric px-2 py-0.5 text-[9px] font-semibold text-white">FINAL · 9:16</span>}
          </div>
          <div className="flex min-w-0 flex-col justify-center gap-2.5 text-xs text-slate-300">
            <div className="flex items-center gap-2"><Scissors className="h-3.5 w-3.5 text-cyan-300" /> Cuts & transitions <span className="ml-auto font-mono text-slate-400">{Math.round(seg(p, 0, 0.45) * 100)}%</span></div>
            <div className="flex items-center gap-2"><Palette className="h-3.5 w-3.5 text-cyan-300" /> Color: Warm +{Math.round(grade * 12)} <span className="ml-auto font-mono text-slate-400">{Math.round(grade * 100)}%</span></div>
            <div className="flex items-center gap-2"><span className="grid h-3.5 w-3.5 place-items-center rounded-sm bg-cyan-300/20 text-[8px] text-cyan-300">T</span> Captions <span className="ml-auto font-mono text-slate-400">{Math.round(seg(p, 0.3, 0.8) * 100)}%</span></div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-gradient-to-r from-electric to-cyan-300" style={{ width: `${p * 100}%` }} /></div>
            <p className="font-mono text-[10px] text-slate-400">{done ? "1080×1920 · 0:28 · H.264 — ready to launch" : "Assembling timeline…"}</p>
          </div>
        </div>
        <div className="relative grid gap-1.5 rounded-xl border border-white/[0.07] bg-black/40 p-2 [grid-template-rows:22px_34px_20px_20px]">
          <Track label="TXT">
            {CAPTIONS.map((c, i) => (
              <span key={c} className="truncate rounded-sm bg-amber-300/80 px-1 text-[8px] leading-[18px] text-black" style={{ width: "25%", opacity: seg(p, 0.3 + i * 0.1, 0.4 + i * 0.1) }}>{c}</span>
            ))}
          </Track>
          <Track label="V1">
            {CLIPS.map((c, i) => {
              const t = seg(p, i * 0.1, i * 0.1 + 0.25);
              return (
                <span key={i} className="relative overflow-hidden rounded-sm border border-electric/60"
                  style={{ width: `${c.w}%`, transform: `translateX(${(1 - t) * c.from}px)`, opacity: 0.25 + t * 0.75, backgroundImage: `url(${c.img})`, backgroundSize: "cover", backgroundPosition: "center" }}>
                  {i > 0 && <span className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-cyan-300" style={{ opacity: t }} />}
                </span>
              );
            })}
          </Track>
          <Track label="A1">
            <span className="flex w-full items-center gap-[2px] overflow-hidden rounded-sm bg-emerald-500/20 px-1">
              {Array.from({ length: 48 }).map((_, i) => <span key={i} className="w-[2px] shrink-0 bg-emerald-400" style={{ height: `${20 + ((i * 37) % 70)}%` }} />)}
            </span>
          </Track>
          <Track label="A2"><span className="w-full rounded-sm bg-violet-400/25" style={{ opacity: seg(p, 0.2, 0.35) }} /></Track>
          <span className="pointer-events-none absolute bottom-1 top-1 w-px bg-red-500" style={{ left: `calc(46px + ${p} * (100% - 56px))` }} />
        </div>
      </div>
    </Window>
  );
}
